import React from 'react';
import { FUNNEL_STAGES, TERMINAL_STATUSES, statusToFunnelOrder } from '../data/funnelStages';

/**
 * ConversionFunnel - Visualizes the hiring funnel from Applied to Offer.
 *
 * Shows:
 * - Meta conversion stats (count + % for each stage)
 * - Horizontal funnel bars scaled to applied count
 * - Stage-to-stage conversion rates
 * - Total rejected count
 *
 * Props:
 *   jobs - array of job objects with `status` (string) and optionally `rejectedAtStage` (string)
 */
const ConversionFunnel = ({ jobs = [] }) => {
  // --- Counting logic ---
  // For each funnel stage, count jobs that reached or passed that stage.
  // A job "reached" a stage if its current funnel order >= that stage's order,
  // OR if it was rejected/withdrawn but its rejectedAtStage order >= that stage's order.

  const stageCounts = FUNNEL_STAGES.map(stage => {
    let count = 0;
    for (const job of jobs) {
      const order = statusToFunnelOrder(job.status);

      if (TERMINAL_STATUSES.includes(job.status)) {
        // Terminal status: only count if rejectedAtStage shows they passed through this stage
        if (job.rejectedAtStage) {
          const rejectedOrder = statusToFunnelOrder(job.rejectedAtStage);
          if (rejectedOrder >= stage.order) {
            count++;
          }
        }
      } else if (order >= stage.order) {
        // Active (non-terminal) job whose current stage is at or past this stage
        count++;
      }
    }
    return { ...stage, count };
  });

  const appliedCount = stageCounts[0]?.count || 0;
  const rejectedCount = jobs.filter(j => j.status === 'rejected').length;

  // Helper: percentage relative to applied count
  const pct = (count) => {
    if (appliedCount === 0) return 0;
    return (count / appliedCount) * 100;
  };

  const formatPct = (count) => {
    const p = pct(count);
    if (p === 0) return '0%';
    if (p === 100) return '100%';
    if (p < 1) return `${p.toFixed(1)}%`;
    return `${p.toFixed(1)}%`;
  };

  // --- Styles ---
  const containerStyle = {
    background: '#fff',
    border: '1px solid #e5e5e5',
    borderRadius: '12px',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const headerStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1a1a',
    margin: '0 0 16px 0',
  };

  // --- Meta stats row ---
  const statsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '8px',
    marginBottom: '24px',
  };

  const statCardStyle = (color) => ({
    textAlign: 'center',
    padding: '10px 6px',
    borderRadius: '8px',
    background: `${color}0D`, // ~5% opacity
    border: `1px solid ${color}33`, // ~20% opacity
  });

  const statValueStyle = (color) => ({
    fontSize: '22px',
    fontWeight: '700',
    color: color,
    lineHeight: '1.2',
  });

  const statLabelStyle = {
    fontSize: '11px',
    color: '#6b7280',
    marginTop: '2px',
  };

  const statPctStyle = {
    fontSize: '11px',
    color: '#9ca3af',
    marginTop: '2px',
  };

  // --- Funnel bars ---
  const barRowStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '6px',
  };

  const barLabelStyle = {
    width: '100px',
    fontSize: '11px',
    fontWeight: '500',
    color: '#374151',
    textAlign: 'right',
    paddingRight: '10px',
    flexShrink: 0,
  };

  const barTrackStyle = {
    flex: 1,
    height: '28px',
    background: '#f3f4f6',
    borderRadius: '6px',
    position: 'relative',
    overflow: 'hidden',
  };

  const barFillStyle = (color, percentage) => ({
    height: '100%',
    width: `${Math.max(percentage, percentage > 0 ? 2 : 0)}%`,
    background: color,
    borderRadius: '6px',
    transition: 'width 0.3s ease',
    minWidth: percentage > 0 ? '2px' : '0',
  });

  const barCountStyle = {
    width: '40px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#374151',
    textAlign: 'right',
    paddingLeft: '8px',
    flexShrink: 0,
  };

  // --- Conversion rates ---
  const conversionRowStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '20px',
    marginBottom: '12px',
  };

  const conversionChipStyle = {
    fontSize: '11px',
    color: '#6b7280',
    background: '#f9fafb',
    border: '1px solid #e5e5e5',
    borderRadius: '6px',
    padding: '4px 8px',
  };

  const conversionValueStyle = {
    fontWeight: '600',
    color: '#374151',
  };

  // --- Rejected footer ---
  const rejectedStyle = {
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid #f3f4f6',
    fontSize: '12px',
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  const rejectedCountStyle = {
    fontWeight: '600',
    color: '#ef4444',
  };

  // If no jobs have entered the funnel, show empty state
  if (appliedCount === 0) {
    return (
      <div style={containerStyle}>
        <h3 style={headerStyle}>Hiring Funnel</h3>
        <p style={{ fontSize: '13px', color: '#9ca3af', textAlign: 'center', padding: '20px 0' }}>
          No applications tracked yet. Apply to jobs to see your funnel.
        </p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h3 style={headerStyle}>Hiring Funnel</h3>

      {/* Meta conversion stats row */}
      <div style={statsGridStyle}>
        {stageCounts.map(stage => (
          <div key={stage.key} style={statCardStyle(stage.color)}>
            <div style={statValueStyle(stage.color)}>{stage.count}</div>
            <div style={statLabelStyle}>{stage.label}</div>
            <div style={statPctStyle}>{formatPct(stage.count)}</div>
          </div>
        ))}
      </div>

      {/* Horizontal funnel bars */}
      <div style={{ marginBottom: '4px' }}>
        {stageCounts.map(stage => (
          <div key={stage.key} style={barRowStyle}>
            <div style={barLabelStyle}>{stage.label}</div>
            <div style={barTrackStyle}>
              <div style={barFillStyle(stage.color, pct(stage.count))} />
            </div>
            <div style={barCountStyle}>{stage.count}</div>
          </div>
        ))}
      </div>

      {/* Stage-to-stage conversion rates */}
      <div style={conversionRowStyle}>
        {stageCounts.slice(0, -1).map((stage, i) => {
          const nextStage = stageCounts[i + 1];
          const rate = stage.count > 0
            ? ((nextStage.count / stage.count) * 100).toFixed(1)
            : '0.0';
          return (
            <div key={stage.key} style={conversionChipStyle}>
              {stage.label} → {nextStage.label}:{' '}
              <span style={conversionValueStyle}>{rate}%</span>
            </div>
          );
        })}
      </div>

      {/* Rejected count */}
      <div style={rejectedStyle}>
        <span>Rejected:</span>
        <span style={rejectedCountStyle}>{rejectedCount}</span>
        {appliedCount > 0 && (
          <span>({((rejectedCount / appliedCount) * 100).toFixed(1)}% of applied)</span>
        )}
      </div>
    </div>
  );
};

export default ConversionFunnel;
